import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { samplesAPI, masterDataAPI } from '../../api';
import { Loading } from '../../components';
import { useToast } from '../../context/ToastContext';
import './CreateSample.css';

const CreateSample = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sampleNames, setSampleNames] = useState([]);
  const [waterSources, setWaterSources] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [fieldParameters, setFieldParameters] = useState([]);
  const [parameterValues, setParameterValues] = useState({});
  const [formData, setFormData] = useState({
    title: '',
    organization: '',
    address: '',
    district: '',
    state: '',
    latitude: '',
    longitude: '',
    sampleName: '',
    waterSource: '',
    locationType: '',
    addressRemark: '',
    remark: '',
    collectedAt: new Date().toISOString().split('T')[0]
  });
  const [sampleImage, setSampleImage] = useState(null);
  const [locationImage, setLocationImage] = useState(null);

  // Fetch master data and field parameters
  useEffect(() => {
    const fetchData = async () => {
      try {
        setInitialLoading(true);
        const [snRes, wsRes, ltRes, paramsRes] = await Promise.all([
          masterDataAPI.getSampleNames().catch(() => ({ data: { data: [] } })),
          masterDataAPI.getWaterSources(),
          masterDataAPI.getLocationTypes(),
          samplesAPI.getFieldParameters()
        ]);
        setSampleNames(snRes.data?.data || []);
        setWaterSources(wsRes.data?.data || []);
        setLocationTypes(ltRes.data?.data || []);
        setFieldParameters(paramsRes.data?.data || []);
      } catch (err) {
        console.log('Failed to load data:', err);
        setError('Failed to load form data. Please refresh.');
      } finally {
        setInitialLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleParameterChange = (paramId, value) => {
    setParameterValues({ ...parameterValues, [paramId]: value });
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === 'sample') {
      setSampleImage(file);
    } else {
      setLocationImage(file);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          });
        },
        (error) => {
          toast.error('Unable to get current location: ' + error.message);
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required images
    if (!sampleImage) {
      setError('Sample image is required');
      return;
    }
    if (!locationImage) {
      setError('Location image is required');
      return;
    }

    // Validate mandatory field parameters are filled
    const missingParams = fieldParameters.filter(p => p.isMandatory !== false && !parameterValues[p._id]);
    if (missingParams.length > 0) {
      setError(`Please fill all mandatory field parameters: ${missingParams.map(p => p.name).join(', ')}`);
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      // Optional title and organization
      if (formData.title) data.append('title', formData.title);
      if (formData.organization) data.append('organization', formData.organization);

      // Build complete address
      const fullAddress = `${formData.address}, ${formData.district}, ${formData.state}`;
      data.append('address', fullAddress);

      // Collection date
      data.append('collectedAt', formData.collectedAt);

      // Location coordinates
      data.append('location', JSON.stringify({
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      }));

      // Sample name, sample source and location type (all independent)
      if (formData.sampleName) data.append('sampleName', formData.sampleName);
      if (formData.waterSource) data.append('waterSource', formData.waterSource);
      if (formData.locationType) data.append('locationType', formData.locationType);
      if (formData.addressRemark) data.append('addressRemark', formData.addressRemark);
      if (formData.remark) data.append('remark', formData.remark);

      // Images
      data.append('sampleImage', sampleImage);
      data.append('locationImage', locationImage);

      // Field parameters
      const parameters = fieldParameters.map(p => ({
        id: p._id,
        value: parameterValues[p._id]
      }));
      data.append('parameters', JSON.stringify(parameters));

      await samplesAPI.create(data);
      navigate('/my-samples');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create sample');
    } finally {
      setLoading(false);
    }
  };

  const renderParameterInput = (param) => {
    const value = parameterValues[param._id] || '';
    const isRequired = param.isMandatory !== false;

    if (param.type === 'ENUM' && param.enumEvaluation) {
      const options = Object.keys(param.enumEvaluation);
      return (
        <select
          className="form-input"
          value={value}
          onChange={(e) => handleParameterChange(param._id, e.target.value)}
          required={isRequired}
        >
          <option value="">Select {param.name}</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        type="number"
        className="form-input"
        value={value}
        onChange={(e) => handleParameterChange(param._id, e.target.value)}
        placeholder={`Enter ${param.name}`}
        step="any"
        required={isRequired}
      />
    );
  };

  if (initialLoading) {
    return <Loading message="Loading form..." />;
  }

  return (
    <div className="create-sample-page">
      <div className="create-sample-header">
        <h1>Create New Sample</h1>
        <p>Submit a new water sample for analysis</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="create-sample-form">
        {/* Basic Information */}
        <div className="form-section card">
          <h2 className="section-title">Sample Information</h2>

          <div className="form-group">
            <label className="form-label">Name (Optional)</label>
            <input
              type="text"
              name="title"
              className="form-input"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Ram Kumar, Shyam Singh"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Organization (Optional)</label>
            <input
              type="text"
              name="organization"
              className="form-input"
              value={formData.organization}
              onChange={handleChange}
              placeholder="e.g., ABC Hospital, XYZ School"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Address *</label>
            <textarea
              name="address"
              className="form-input form-textarea"
              value={formData.address}
              onChange={handleChange}
              required
              placeholder="e.g., 123, Main Street, Near Bus Stand, Anytown - 123456"
              rows="2"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">District *</label>
              <input
                type="text"
                name="district"
                className="form-input"
                value={formData.district}
                onChange={handleChange}
                required
                placeholder="e.g., Varanasi"
              />
            </div>

            <div className="form-group">
              <label className="form-label">State *</label>
              <input
                type="text"
                name="state"
                className="form-input"
                value={formData.state}
                onChange={handleChange}
                required
                placeholder="e.g., Uttar Pradesh"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Sample Name *</label>
              <select
                name="sampleName"
                className="form-input"
                value={formData.sampleName}
                onChange={handleChange}
                required
              >
                <option value="">Select Sample Name</option>
                {sampleNames.map(sn => (
                  <option key={sn._id} value={sn.name}>{sn.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Sample Source *</label>
              <select
                name="waterSource"
                className="form-input"
                value={formData.waterSource}
                onChange={handleChange}
                required
              >
                <option value="">Select Sample Source</option>
                {waterSources.map(ws => (
                  <option key={ws._id} value={ws.name}>{ws.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Location Type *</label>
              <select
                name="locationType"
                className="form-input"
                value={formData.locationType}
                onChange={handleChange}
                required
              >
                <option value="">Select Location Type</option>
                {locationTypes.map(lt => (
                  <option key={lt._id} value={lt.name}>{lt.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Collection Date *</label>
            <input
              type="date"
              name="collectedAt"
              className="form-input"
              value={formData.collectedAt}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Address Remark (Optional)</label>
            <textarea
              name="addressRemark"
              className="form-input form-textarea"
              value={formData.addressRemark}
              onChange={handleChange}
              placeholder="Additional address details (flat number, building name, etc.)"
              rows="2"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Remark / Observation (Optional)</label>
            <textarea
              name="remark"
              className="form-input form-textarea"
              value={formData.remark}
              onChange={handleChange}
              placeholder="Enter remark or observation to appear in the PDF report..."
              rows="2"
            />
          </div>
        </div>

        {/* GPS Coordinates */}
        <div className="form-section card">
          <h2 className="section-title">GPS Coordinates</h2>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Latitude *</label>
              <input
                type="number"
                name="latitude"
                className="form-input"
                value={formData.latitude}
                readOnly
                step="any"
                placeholder="Click 'Get Current Location'"
                style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Longitude *</label>
              <input
                type="number"
                name="longitude"
                className="form-input"
                value={formData.longitude}
                readOnly
                step="any"
                placeholder="Click 'Get Current Location'"
                style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
              />
            </div>

            <button type="button" className="btn btn-secondary get-location-btn" onClick={getCurrentLocation}>
              Get Current Location
            </button>
          </div>
          <small className="form-help" style={{ color: '#6b7280', marginTop: '-8px', display: 'block' }}>
            GPS coordinates are captured automatically and cannot be edited manually to maintain data authenticity.
          </small>
        </div>

        {/* Field Parameters */}
        {fieldParameters.length > 0 && (
          <div className="form-section card">
            <h2 className="section-title">Field Test Parameters</h2>
            <div className="parameters-grid">
              {fieldParameters.map(param => (
                <div key={param._id} className="form-group parameter-input">
                  <label className="form-label">
                    {param.name} {param.unit && `(${param.unit})`} {param.isMandatory !== false ? '*' : '(Optional)'}
                  </label>
                  {renderParameterInput(param)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Images */}
        <div className="form-section card">
          <h2 className="section-title">Sample Images</h2>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Sample Image *</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={(e) => handleFileChange(e, 'sample')}
                className="form-input file-input"
                required
              />
              {sampleImage && <span className="file-name">{sampleImage.name}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Location Image *</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={(e) => handleFileChange(e, 'location')}
                className="form-input file-input"
                required
              />
              {locationImage && <span className="file-name">{locationImage.name}</span>}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Sample'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateSample;
